// Tracks chats that are awaiting a tutor response, surviving a ChatScreen
// unmount/remount.
//
// ChatScreen is fully recreated when the user navigates to another tab (My
// Course / My Chats) and back — there's no <keep-alive> and the dashboard
// RouterView keys components by route path — so its local "waiting for the
// tutor" state (disableRecordButton + the loading bubble) is otherwise lost,
// re-enabling the record mic and letting the user send a second message before
// the tutor has replied.
//
// We can't recover that state from RTDB alone: the backend persists the
// student's message only after upload + transcription (a few seconds) and
// writes no "responding" flag, so a quick navigation can leave the last
// persisted message as the *previous* tutor turn. We therefore remember the key
// of the message we replied to, so a remount can tell whether a *new* tutor
// message has since arrived.
//
// In-memory (module singleton): it survives in-app navigation, which is where
// the bug occurs. A hard reload clears it, which is acceptable — by then the
// backend has usually caught up and ChatScreen's last-persisted-message check
// takes over.

const awaiting = new Map(); // dialogId -> { repliedToKey: string | null, sentAt: number }

export const pendingTutorResponses = {
  // Mark that a message was sent in `dialogId` and we're awaiting the tutor.
  // `repliedToKey` is the key of the last persisted message at send time (the
  // tutor turn being replied to), or null for the opening message.
  markAwaiting(dialogId, repliedToKey = null) {
    if (!dialogId) return;
    // sentAt anchors ChatScreen's reply watchdog to the turn rather than to the
    // component: without it, every remount granted a fresh full window, so
    // reopening the chat repeatedly could postpone the recovery indefinitely.
    awaiting.set(dialogId, { repliedToKey, sentAt: Date.now() });
  },

  // Returns the pending record for `dialogId`, or null if none is awaiting.
  get(dialogId) {
    return awaiting.get(dialogId) || null;
  },

  clear(dialogId) {
    awaiting.delete(dialogId);
  },

  clearAll() {
    awaiting.clear();
  },
};

// ---------------------------------------------------------------------------
// Reply-lag anchors
// ---------------------------------------------------------------------------
//
// A parallel, dedicated record that measures how long a learner waits for a
// reply, on the one clock that matters: the device's own. It writes two
// elapsed durations onto the tutor message node (`clientTextLagMs`,
// `clientAudioLagMs`), which the server converts into the tutor-replyTextLag
// and tutor-replyAudioLag metrics.
//
// It is deliberately NOT folded into `awaiting` above, even though both are
// keyed by dialog, because the two records answer different questions and have
// different lifetimes:
//   - `awaiting` is one dialog-keyed record whose whole job is mic gating and
//     the reply watchdog. A second send overwrites it, and six call sites
//     clear it. None of that is touched here.
//   - an anchor belongs to ONE message. It is created only at a real voice
//     send tap, binds to the reply message when that reply arrives, and is
//     consumed by the first play of that message's audio. A second send while
//     the first reply's audio is still pending is a normal case (the mic
//     re-enables as soon as the tutor TEXT lands), so a bound anchor must
//     survive it.
//
// `markAwaiting` is also stamped by two call sites that are not send taps at
// all (a brand-new chat, and the on-remount "we cannot know when that send
// happened" recovery), which is the other reason it cannot serve as the
// anchor: both would report a wait nobody actually waited.

// The walk-away guard, applied at the source. A bound anchor that is not
// consumed within this long after the text rendered is discarded and nothing
// is written — the learner put the phone down, and a lag that includes that
// gap is not reply latency. Only the client can make this call: both stamps
// sit on the device clock, so the server cannot tell a slow reply from an
// absent learner.
export const REPLY_LAG_EXPIRY_MS = 120000;

// dialogId -> tapAtMs. At most one unbound anchor per dialog: the newest send
// is the only one whose reply has not arrived yet.
const pendingAnchors = new Map();
// `${dialogId}/${messageKey}` -> { tapAtMs, textRenderedAtMs }. Bound anchors
// are keyed by MESSAGE, which is what makes a second send unable to clobber
// one.
const boundAnchors = new Map();
// dialogId -> the device time until which no reply in that dialog is
// measured. Set when the client gives up on a send whose reply the backend
// may still write: a connection that dropped during the upload, a 5xx, or the
// reply watchdog. If the learner records again meanwhile, that late reply is
// the next tutor message to arrive, and binding it to the NEW send's tap would
// report a falsely short wait for exactly the slow turn the metric exists to
// catch. The store cannot tell the late reply from the new one, so it
// measures neither: losing a sample is the safe direction, a wrong one is not.
// The window is the anchor expiry, because a reply bound later than that is
// discarded by the walk-away guard anyway.
const unmeasurableUntil = new Map();

function boundKey(dialogId, messageKey) {
  return `${dialogId}/${messageKey}`;
}

// Best-effort, fire-and-forget, and identical in shape to the seenAt write in
// TutorMessage.markMessageAsSeen: the owner writes onto its own message node,
// server-side conversion turns it into a metric. A failure here must never
// surface to the learner — the chat is unaffected either way.
// One dynamic import for the module's lifetime, shared by both writes. The
// firebase/database chunk is loaded lazily (the seenAt write does the same)
// but resolving it once means two stamps a second apart do not each pay a
// module resolution, and the second write cannot start before the first has
// finished resolving.
let databaseModulePromise = null;

function loadDatabaseModule() {
  if (!databaseModulePromise) {
    databaseModulePromise = import("firebase/database");
  }
  return databaseModulePromise;
}

function writeLagField(dialogId, messageKey, field, valueMs) {
  loadDatabaseModule()
    .then(({ getDatabase, ref, update }) =>
      update(ref(getDatabase(), `messages/${dialogId}/${messageKey}`), {
        [field]: valueMs,
      })
    )
    .catch((error) => {
      console.error(`Failed to record ${field}:`, error);
    });
}

// Drop anchors nobody will ever consume, so a long session cannot grow either
// map without bound. Called on the paths that add entries.
function pruneExpiredAnchors(nowMs) {
  for (const [key, anchor] of boundAnchors) {
    if (nowMs - anchor.textRenderedAtMs > REPLY_LAG_EXPIRY_MS) {
      boundAnchors.delete(key);
    }
  }
  for (const [dialogId, tapAtMs] of pendingAnchors) {
    if (nowMs - tapAtMs > REPLY_LAG_EXPIRY_MS) {
      pendingAnchors.delete(dialogId);
    }
  }
  for (const [dialogId, untilMs] of unmeasurableUntil) {
    if (nowMs > untilMs) {
      unmeasurableUntil.delete(dialogId);
    }
  }
}

export const replyLagAnchors = {
  // The ONLY creation point: the real voice send tap (ChatScreen.newRecordedAudio).
  // Interaction submits, new-chat setup and remount recovery never call this,
  // so those turns write no lag fields at all.
  // tapAtMs is the moment the learner tapped send, which AudioRecorder
  // measures and hands over. It is NOT the moment this runs: the recorder
  // awaits finalizeCapture in between, and that can take seconds. A caller
  // with nothing usable to pass falls back to now, which is the old behavior
  // and only ever under-reports.
  // Returns the tap time it actually stored, which is what a later
  // cancelTurn needs in order to cancel THIS send rather than whatever is
  // armed by the time the cancellation runs. Null when nothing was armed.
  startTurn(dialogId, tapAtMs) {
    if (!dialogId) return null;
    const now = Date.now();
    const usable =
      typeof tapAtMs === "number" &&
      isFinite(tapAtMs) &&
      tapAtMs <= now &&
      now - tapAtMs <= REPLY_LAG_EXPIRY_MS;
    pruneExpiredAnchors(now);
    const armedAt = usable ? tapAtMs : now;
    pendingAnchors.set(dialogId, armedAt);
    return armedAt;
  },

  // The tutor reply for that send has arrived and rendered. Move the anchor
  // onto the message key and stamp the text lag. No pending anchor means this
  // reply belongs to a turn we never anchored (a reload mid-turn, or a turn
  // that was not a voice send), and nothing is written.
  bindToReply(dialogId, messageKey) {
    if (!dialogId || !messageKey) return;
    const now = Date.now();
    const suppressedUntil = unmeasurableUntil.get(dialogId);
    if (suppressedUntil !== undefined) {
      if (now <= suppressedUntil) {
        // This reply may belong to the abandoned send rather than to the one
        // that is armed, so it is not measured, and the armed anchor goes
        // with it rather than surviving to bind a reply further on.
        pendingAnchors.delete(dialogId);
        return;
      }
      unmeasurableUntil.delete(dialogId);
    }
    const tapAtMs = pendingAnchors.get(dialogId);
    if (tapAtMs === undefined) return;
    pendingAnchors.delete(dialogId);
    pruneExpiredAnchors(now);
    // The same walk-away guard the bound anchor gets, applied one step
    // earlier. An app suspended for minutes keeps advancing Date.now(), so a
    // reply that lands on resume would otherwise report the suspension as
    // reply latency. 120 seconds is not an arbitrary bound here either: it is
    // the reply watchdog's own window (REPLY_WATCHDOG_MS in ChatScreen), after
    // which the component has already toasted the turn as failed and handed
    // the mic back. The accepted cost is that a turn slower than that
    // contributes no sample at all — a turn the product has already declared
    // dead is not a latency measurement worth keeping.
    if (now - tapAtMs > REPLY_LAG_EXPIRY_MS) return;
    boundAnchors.set(boundKey(dialogId, messageKey), {
      tapAtMs,
      textRenderedAtMs: now,
    });
    writeLagField(dialogId, messageKey, "clientTextLagMs", now - tapAtMs);
  },

  // The audio-start stamp. Called at the moment the shared playback service
  // actually starts this message's reply audio — not when its bytes are
  // fetched. Consumes the anchor, so a later manual replay writes nothing.
  recordAudioStart(dialogId, messageKey) {
    if (!dialogId || !messageKey) return;
    const key = boundKey(dialogId, messageKey);
    const anchor = boundAnchors.get(key);
    if (!anchor) return;
    boundAnchors.delete(key);
    const now = Date.now();
    if (now - anchor.textRenderedAtMs > REPLY_LAG_EXPIRY_MS) return;
    writeLagField(dialogId, messageKey, "clientAudioLagMs", now - anchor.tapAtMs);
  },

  // Drop an ARMED anchor whose turn is over without a reply: the send
  // failed, the reply watchdog gave up, or the learner answered an
  // interaction card instead of re-recording. Bound anchors are untouched —
  // one of those belongs to a reply that already arrived, and its audio stamp
  // is still owed. Without this, a dead send's anchor stays live for the full
  // 120 seconds and the next tutor reply to arrive for that dialog — an
  // interaction-card reply, say, which arms no anchor of its own by design —
  // would bind it and report a wait that spans the failure, the learner's
  // realization and a different turn.
  // tapAtMs identifies WHICH send is being cancelled, and is what stops a
  // slow first request's failure handler deleting a second send's anchor: an
  // upload can outlive the reply watchdog, the learner can record again, and
  // only then does the first request reject. Pass the value startTurn
  // returned. Omit it (or pass null) to cancel whatever is armed, which is
  // what the watchdog and the interaction-submit path want — neither is tied
  // to one request.
  // `replyMayStillArrive` says the client gave up on a request the backend
  // may still complete (anything but a 4xx refusal, which is answered before
  // the agent runs). It stops the next reply-lag window in this dialog from
  // measuring anything; see `unmeasurableUntil`. It is recorded even when the
  // named send no longer owns the armed anchor, because that is exactly the
  // case where a late reply would bind the newer send's tap.
  cancelTurn(dialogId, tapAtMs, { replyMayStillArrive = false } = {}) {
    if (!dialogId) return;
    if (replyMayStillArrive) {
      unmeasurableUntil.set(dialogId, Date.now() + REPLY_LAG_EXPIRY_MS);
    }
    if (typeof tapAtMs === "number" && pendingAnchors.get(dialogId) !== tapAtMs) {
      return;
    }
    pendingAnchors.delete(dialogId);
  },

  clearAll() {
    pendingAnchors.clear();
    boundAnchors.clear();
    unmeasurableUntil.clear();
  },
};
